import { registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClientModule
} from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { provideCharts, withDefaultRegisterables, BaseChartDirective } from 'ng2-charts';
import { AngularOpenlayersModule } from 'ngx-openlayers';
import { AppComponent } from './app.component';
import { AppExceptionHandler } from './app.exception.handler';
import { localeFactory } from './locale-factory';
import {
  AuthorizeRequestInterceptor,
  AuthorizeRequestInterceptorFactoryProvider,
  HttpExceptionInterceptor,
  HttpExceptionInterceptorFactoryProvider,
  PendingRequestInterceptor,
  PendingRequestInterceptorFactoryProvider,
  CustomUrlSerializer
} from './shared';
import { SessionService } from './shared/session.service';
import { SharedModule } from './shared/shared.module';
import { RoutingModule } from './structure';
import { StructureModule } from './structure/structure.module';

import { UrlSerializer } from '@angular/router';

import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import localeNl from '@angular/common/locales/nl';
import localeDe from '@angular/common/locales/de';
import localeFi from '@angular/common/locales/fi';
import localeDa from '@angular/common/locales/da';
import localeSv from '@angular/common/locales/sv';
// We don't support the Russian language but adding this to solve an issue where several of our users have their browser set to Russian.
import localeRu from '@angular/common/locales/ru';
// English-Austrailian is imported and used for date/time but other translations will fall back to English in production
import localeEnAu from '@angular/common/locales/en-AU';
import { ErrorService } from './shared/errors/error.service';

import { APP_INITIALIZER } from '@angular/core';
import { TranslationBootstrapperService } from './translation-bootstrapper.service';

export function initTranslations(translationBooststrapperService: TranslationBootstrapperService) {
  return () => translationBooststrapperService.init();
}

registerLocaleData(localeFr, 'fr');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeNl, 'nl');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeFi, 'fi');
registerLocaleData(localeRu, 'ru');
registerLocaleData(localeEnAu, 'en-au');
registerLocaleData(localeDa, 'da');
registerLocaleData(localeSv, 'sv');

const PendingRequestInterceptorExistingProvider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: PendingRequestInterceptor,
  multi: true
};

const AuthorizeRequestInterceptorExistingProvider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: AuthorizeRequestInterceptor,
  multi: true
};

const HttpExceptionInterceptorExistingProvider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: HttpExceptionInterceptor,
  multi: true
};

//Outdated browsers, particularly on old iPhones, do not support animation and will throw errors if it is not disabled
const disableAnimations =
  !('animate' in document.documentElement)
  || (navigator && /iPhone OS (8|9|10|11|12|13)_/.test(navigator.userAgent));

@NgModule({
  declarations: [AppComponent],
  imports: [
    NgbModule,
    BrowserModule,
    BrowserAnimationsModule.withConfig({ disableAnimations: disableAnimations }),
    StructureModule,
    RoutingModule,
    HttpClientModule,
    SharedModule.forRoot(),
    BaseChartDirective,
    AngularOpenlayersModule,
    TranslateModule.forRoot()
  ],
  exports: [],
  providers: [
    { provide: ErrorHandler, useClass: AppExceptionHandler },
    PendingRequestInterceptorFactoryProvider,
    PendingRequestInterceptorExistingProvider,
    AuthorizeRequestInterceptorExistingProvider,
    AuthorizeRequestInterceptorFactoryProvider,
    HttpExceptionInterceptorExistingProvider,
    HttpExceptionInterceptorFactoryProvider,
    {
      provide: LOCALE_ID,
      deps: [SessionService, ErrorService],
      useFactory: localeFactory
    },
    {
      provide: UrlSerializer,
      useClass: CustomUrlSerializer
    },
    provideCharts(withDefaultRegisterables()),
    {
      provide: APP_INITIALIZER,
      useFactory: initTranslations,
      deps: [TranslationBootstrapperService],
      multi: true
    }
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent]
})
export class AppModule { }
